import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useManagedQuery } from 'utils/managedHooks';
import { useLocation } from 'react-router-dom';

import { Spinner } from 'admin-components/components';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import { INBOUND, OUTBOUND, BOTH } from 'constants/booking';
import { getBookingPaymentURL } from 'utils/urls';
import { GET_EXTRA_PRODUCTS, GET_LUGGAGE_PRODUCTS } from './queries';
import AddAssistanceForm from './AddAssistanceForm';
import { Column } from '../Layout';
import { bookingStatus } from 'types/booking';
import { transformLuggageToExtras } from './AddAssistanceForm/utils';
import { isEurostarOrganization } from 'utils/user';

const AddAssistance = ({
  shouldConfirmBooking,
  setLoading,
  setIsConfirmDisabled,
  isReturn,
  history,
  bookingData: {
    reference,
    currency,
    passengers,
    hotels = [],
    outboundExtras = [],
    inboundExtras = [],
    inbound,
    outbound,
    status,
    agent,
    allBookingItems = [],
    isGroupBooking
  },
  refetchBooking,
  shouldUseReviewBookingScreen = false,
  showAssistance = false,
  showExtras = false,
  showHotel = false,
  passengerId = '',
  user = {}
}) => {
  const { state = {} } = useLocation();
  const { isPackageBooking = false } = state;
  const isAgentEurostarOrganization = isEurostarOrganization(user);

  const formatSegments = () =>
    outbound
      .map((o) => ({ ...o, direction: 'outbound' }))
      .concat(inbound.map((i) => ({ ...i, direction: 'inbound' })))
      .filter((o) => !o.cancelled)
      .map((s) => ({
        id: s.id,
        direction: s.direction,
        origin: s.origin.uic,
        destination: s.destination.uic,
        serviceName: s.trainNumber,
        startValidityDate: s.timing.date,
        startValidityTime: s.timing.departs
      }));

  const activePassengers = useMemo(() => {
    const travelingPassengers = new Set();

    const items = passengerId ? allBookingItems.filter((abi) => abi.passengerId === passengerId) : allBookingItems;

    items.forEach(({ passengerId, cancelled }) => {
      if (!cancelled) {
        travelingPassengers.add(passengerId);
      }
    });

    return passengers.filter(({ id }) => travelingPassengers.has(id));
  }, [allBookingItems, passengers, passengerId]);

  const formatPassengers = () =>
    activePassengers.map((p) => ({
      id: p.id,
      type: p.type,
      disabilityType: p.disabilityType
    }));

  const getDirection = (segments) => {
    const hasOutbound = outbound.some((o) => segments.includes(o.id));
    const hasInbound = inbound.some((i) => segments.includes(i.id));

    if (hasOutbound && hasInbound) {
      return BOTH;
    }
    if (hasOutbound) {
      return OUTBOUND;
    }

    return INBOUND;
  };

  const formatExtras = (extras) =>
    extras.map((e) => ({
      ...e,
      direction: getDirection(e.segments)
    }));

  const { loading, data } = useManagedQuery(GET_EXTRA_PRODUCTS, {
    variables: {
      currency,
      segments: formatSegments(),
      passengers: formatPassengers(),
      types: [],
      isAftersales: false
    },
    skip: !showAssistance
  });

  const uncanceledOutbound = passengerId
    ? outbound
        .filter(({ cancelled }) => !cancelled)
        ?.filter((o) => o.products.some((l) => l.canBeRebooked && !l.cancelled && l.passengerId === passengerId)) ?? []
    : outbound.filter(({ cancelled }) => !cancelled) ?? [];
  const uncanceledInbound = passengerId
    ? inbound
        .filter(({ cancelled }) => !cancelled)
        ?.filter((o) => o.products.some((l) => l.canBeRebooked && !l.cancelled && l.passengerId === passengerId)) ?? []
    : inbound.filter(({ cancelled }) => !cancelled) ?? [];
  const bothDirection = uncanceledOutbound?.length > 0 && uncanceledInbound?.length > 0;
  const outboundOnly = uncanceledOutbound?.length > 0 && uncanceledInbound?.length === 0;
  const items = passengerId ? allBookingItems.filter((abi) => abi.passengerId === passengerId) : allBookingItems;

  const { data: luggageProducts, loading: luggageProductsLoading } = useManagedQuery(GET_LUGGAGE_PRODUCTS, {
    variables: {
      currency,
      exchangeItems: items.filter(({ cancelled, canBeRebooked }) => !cancelled && canBeRebooked).map(({ itemRef }) => Number(itemRef)),
      reference,
      origin: bothDirection || outboundOnly ? uncanceledOutbound?.[0]?.origin?.uic : uncanceledInbound?.[0]?.origin?.uic,
      destination:
        bothDirection || outboundOnly
          ? uncanceledOutbound?.[uncanceledOutbound?.length - 1]?.destination?.uic
          : uncanceledInbound?.[uncanceledInbound?.length - 1]?.destination?.uic,
      passengers: formatPassengers(),
      ...(uncanceledOutbound?.length > 0 && { outboundDate: uncanceledOutbound?.[0]?.timing?.date }),
      ...(uncanceledInbound?.length > 0 && { inboundDate: uncanceledInbound?.[0]?.timing?.date }),
      trainNumbers: Array.from(new Set([...uncanceledOutbound.map((o) => o.trainNumber), ...uncanceledInbound.map((o) => o.trainNumber)]))
    },
    fetchPolicy: 'network-only',
    skip: !showExtras
  });

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';

  const nextRoute = () => {
    if ([bookingStatus.ON_HOLD, bookingStatus.TEMPORARILY_ON_HOLD].includes(status)) {
      return history.push(`/manage/bookings/${reference}/assistance${membershipNumberUrl}`);
    }

    if (shouldUseReviewBookingScreen) {
      if (!showExtras) {
        return history.push(`/booking/${reference}/review-booking${membershipNumberUrl}`);
      }

      return history.push(`/manage/bookings/${reference}/extras/review${membershipNumberUrl}`);
    }

    return history.push(getBookingPaymentURL(reference, membershipNumber));
  };

  if (loading || luggageProductsLoading) {
    return (
      <Column alignItems="center" justifyContent="center">
        <Spinner />
      </Column>
    );
  }

  const shouldShowAssistance = showAssistance || (!showExtras && !showHotel);

  const transformedLuggageExtras = luggageProducts
    ? transformLuggageToExtras(luggageProducts, outbound, inbound, true)
    : {
        outbound: [],
        inbound: []
      };

  return (
    <AddAssistanceForm
      isPackageBooking={isPackageBooking}
      hasActiveHotel={!!hotels.find((h) => !h.cancelled)}
      assistanceOptions={formatExtras(data?.getExtraProducts?.specialAssistance ?? [])}
      outboundExtrasOptions={transformedLuggageExtras.outbound}
      inboundExtrasOptions={transformedLuggageExtras.inbound}
      outboundExtras={outboundExtras}
      inboundExtras={inboundExtras}
      bookingReference={reference}
      refetchBooking={refetchBooking}
      onNext={nextRoute}
      passengers={activePassengers}
      currency={currency}
      setLoading={setLoading}
      setIsConfirmDisabled={setIsConfirmDisabled}
      isReturn={isReturn}
      shouldConfirmBooking={shouldConfirmBooking && [bookingStatus.ON_HOLD, bookingStatus.TEMPORARILY_ON_HOLD].includes(status) === false}
      isEurostarOrganization={isAgentEurostarOrganization}
      isGroupBooking={isGroupBooking}
      showAssistance={shouldShowAssistance}
      showExtras={showExtras}
      showHotel={showHotel}
    />
  );
};

AddAssistance.propTypes = {
  history: historyShape,
  bookingData: bookingShape,
  setLoading: PropTypes.func,
  setIsConfirmDisabled: PropTypes.func,
  refetchBooking: PropTypes.func,
  isReturn: PropTypes.bool,
  shouldConfirmBooking: PropTypes.bool,
  shouldUseReviewBookingScreen: PropTypes.bool,
  isManageExtras: PropTypes.bool,
  showAssistance: PropTypes.bool,
  showExtras: PropTypes.bool,
  showHotel: PropTypes.bool,
  passengerId: PropTypes.string,
  user: userShape
};

export default AddAssistance;
